import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, KeyRound, RefreshCw, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth, authApi } from "@/lib/auth";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { BookingsTable } from "@/components/admin/BookingsTable";
import { EnquiriesList } from "@/components/admin/EnquiriesList";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";

const TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "cancelled", label: "Cancelled" },
  { id: "enquiries", label: "Enquiries" },
];

const Stat = ({ label, value, testid }) => (
  <div className="border-t border-gold/40 pt-4" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{label}</p>
    <p className="mt-2 font-display text-3xl text-white">{value}</p>
  </div>
);

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [pwOpen, setPwOpen] = useState(false);

  const load = async () => {
    try {
      const [b, e] = await Promise.all([authApi.get("/admin/bookings"), authApi.get("/admin/enquiries")]);
      setBookings(b.data);
      setEnquiries(e.data);
    } catch (_) {
      toast.error("Couldn't load dashboard data.");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const now = Date.now();
  const upcoming = (bookings || []).filter((b) => b.status === "confirmed" && new Date(b.slot_start) > now);
  const past = (bookings || []).filter((b) => b.status === "confirmed" && new Date(b.slot_start) <= now).reverse();
  const cancelled = (bookings || []).filter((b) => b.status === "cancelled").reverse();
  const onUpdate = (updated) => setBookings((bs) => bs.map((b) => (b.id === updated.id ? updated : b)));

  return (
    <div className="relative min-h-screen bg-ink text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4 lg:px-10">
          <Link to="/" aria-label="AiCendor home" data-testid="admin-logo-home"><Logo /></Link>
          <div className="flex items-center gap-6 font-mono text-[11px] uppercase tracking-[0.22em]">
            <span className="hidden text-slate-500 sm:inline" data-testid="admin-user-email">{user.email}</span>
            <button type="button" data-testid="admin-refresh" onClick={load} className="link-underline flex items-center gap-2 text-slate-300 hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
            <button type="button" data-testid="admin-change-password" onClick={() => setPwOpen(true)} className="link-underline flex items-center gap-2 text-slate-300 hover:text-white"><KeyRound className="h-3.5 w-3.5" /> Password</button>
            <button type="button" data-testid="admin-logout" onClick={logout} className="link-underline flex items-center gap-2 text-slate-300 hover:text-white"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-20" data-testid="admin-dashboard">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Owner dashboard</p>
        <h1 className="mt-5 font-display text-4xl tracking-tight sm:text-5xl">Consultations &amp; enquiries</h1>

        {bookings === null || enquiries === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <>
            <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4">
              <Stat label="Upcoming calls" value={upcoming.length} testid="admin-stat-upcoming" />
              <Stat label="Completed" value={past.length} testid="admin-stat-past" />
              <Stat label="Cancelled" value={cancelled.length} testid="admin-stat-cancelled" />
              <Stat label="Enquiries" value={enquiries.length} testid="admin-stat-enquiries" />
            </div>

            <div className="mt-16 flex flex-wrap gap-8 border-b border-white/10">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`-mb-px border-b-2 pb-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${tab === t.id ? "border-gold text-gold" : "border-transparent text-slate-500 hover:text-white"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-2">
              {tab === "upcoming" && <BookingsTable bookings={upcoming} onUpdate={onUpdate} />}
              {tab === "past" && <BookingsTable bookings={past} onUpdate={onUpdate} />}
              {tab === "cancelled" && <BookingsTable bookings={cancelled} onUpdate={onUpdate} />}
              {tab === "enquiries" && <EnquiriesList enquiries={enquiries} />}
            </div>
          </>
        )}
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }
  return user ? <Dashboard /> : <AdminLogin />;
}
